import { Play } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "../lib/cn";
import { Button, type ButtonProps } from "./button";

/**
 * Every "start a run" action in the product, with one vocabulary and one look:
 * - `run`: start a job now (Jobs table, job drawer)
 * - `again`: run this finished run's job again from the start (run detail)
 * - `from-failure`: restart a failed run at the failed step
 * - `failed-tests`: re-run only the tests that failed
 * - `once`: run a job once with overridden commands
 * The most likely action on a screen is `variant="primary"`; the rest stay default.
 */
export type RunIntent = "run" | "again" | "from-failure" | "failed-tests" | "once";

export const runLabels: Record<RunIntent, string> = {
  run: "Run now",
  again: "Run again",
  "from-failure": "Run from failure",
  "failed-tests": "Run failed tests only",
  once: "Run once",
};

export interface RunButtonProps extends Omit<ButtonProps, "children"> {
  intent: RunIntent;
  /** Hide the play icon (dense tables). */
  iconless?: boolean;
}

export const RunButton = forwardRef<HTMLButtonElement, RunButtonProps>(
  ({ intent, iconless, className, size, ...props }, ref) => (
    <Button ref={ref} size={size} className={cn("whitespace-nowrap", className)} {...props}>
      {iconless ? null : <Play className={size === "sm" ? "size-3" : "size-3.5"} aria-hidden />}
      {runLabels[intent]}
    </Button>
  ),
);
RunButton.displayName = "RunButton";
